import {
  AssignedProperty,
  AssignedPropertyValue,
  Assignee,
  AssigneeType,
  DefaultType,
  PostStatusPropertyValue,
  Property,
  PropertyClient,
  PropertyValue,
} from 'api/clients/PropertyClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useProperties } from 'hooks/useProperties'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useDispatch, useSelector } from 'react-redux'
import { upsertFilesProperties } from 'redux/actions/filesProperties'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { createAssignedPropertyValue } from 'screens/Finder/components/FileProperty/FileProperty'
import { RootState } from 'types'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'

import { useState, useCallback } from 'react'
import { useRootStore } from 'stores/RootStore'
import {
  ScheduledPostTarget,
  useScheduledPostTargets,
  useUpdateScheduledPostTarget,
} from 'hooks/queries/scheduledPostTargets'

const STATUS_TO_TARGET_STATUS: Record<PostStatusPropertyValue, ScheduledPostTarget['status']> = {
  [PostStatusPropertyValue.AutoPostScheduled]: 'pending',
  [PostStatusPropertyValue.ReminderScheduled]: 'pending',
  [PostStatusPropertyValue.Draft]: 'pending',
  [PostStatusPropertyValue.Posted]: 'published',
  [PostStatusPropertyValue.Failed]: 'failed',
}

export const usePostProperties = (assignee: Assignee, projectId?: string) => {
  const workspaceId = useCurrentWorkspaceId()
  const { updatePropertyValues, getAssigneesForUpdate } = useFinderContext()
  const { isIOSNative } = useResponsiveDesign()
  const rootStore = useRootStore()
  const file = rootStore.files.get(assignee.id)
  const { data: targets = [] } = useScheduledPostTargets(file?.scheduled_post_id as number, { enablePolling: true })
  const { mutateAsync: updateScheduledPostTarget } = useUpdateScheduledPostTarget()
  const { currentFolderProperties, currentWorkspaceProperties, projectProperties } = useProperties(file?.parent?.id)
  const dispatch = useDispatch()

  const postStatusProperty = currentWorkspaceProperties.find((p) => p.default_type === DefaultType.PostStatus)
  const currentFolderPostAtProperty = currentFolderProperties.find((p) => p.name === 'Post Date & Time')

  const postFolderProperties = projectId ? projectProperties(projectId) : []
  const projectPostAtProperty = postFolderProperties?.find((p) => p.name === 'Post Date & Time')

  const properties = useSelector(({ filesProperties }: RootState) => filesProperties[assignee.id] || {})

  const workspace = useCurrentWorkspace()

  // State to hold the optimistic status update
  const [optimisticStatus, setOptimisticStatus] = useState<PostStatusPropertyValue | null>(null)

  const findOrCreateAssignedProperty = useCallback(async (assignee: Assignee, property: Property) => {
    const response = await PropertyClient.findOrCreateAssignedProperty<AssignedProperty>(
      assignee.id,
      property,
      assignee.type,
    )

    return response.data.assigned_property
  }, [])

  const createPropertyValue = useCallback(
    async (propertyValue: { value: any; property_id: number }) => {
      if (!workspace) return

      const response = await PropertyClient.createPropertyValue(workspace.key, propertyValue as PropertyValue)

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        return response.data.property_value
      }
    },
    [workspace],
  )

  const createAndAssignProperty = useCallback(
    async (fileId: string, type: AssigneeType, date: string | null) => {
      const calendarProperty = currentFolderPostAtProperty || projectPostAtProperty

      if (!calendarProperty) return

      const assignee = { id: fileId, type }
      const assignedProperty = await findOrCreateAssignedProperty(assignee, calendarProperty)
      const propertyValue = await createPropertyValue({
        value: date,
        property_id: calendarProperty.id,
      })
      if (propertyValue && assignedProperty) {
        const res = await createAssignedPropertyValue(assignedProperty.id, calendarProperty, [propertyValue])
        if (res && res.type === SUCCESS_RESPONSE_TYPE) {
          dispatch(upsertFilesProperties([res.data.assigned_property]))
        }
      }
    },
    [currentFolderPostAtProperty, projectPostAtProperty, findOrCreateAssignedProperty, createPropertyValue, dispatch],
  )

  const updatePostAt = async (dateTime: string | null) => {
    // If there is no current project, use the post's parent project's property
    const postAtProperty = currentFolderPostAtProperty || projectPostAtProperty

    if (!postAtProperty) return

    const propertyValue = properties[postAtProperty.id]?.values?.[0]

    if (!propertyValue) {
      await createAndAssignProperty(assignee.id, assignee.type, dateTime)
      return
    }

    const response = await PropertyClient.updatePropertyValue(workspaceId.toString(), {
      ...propertyValue,
      value: dateTime,
    } as PropertyValue)

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      const updatedPropertyValues = postAtProperty.property_values.map((pv) => {
        if (pv.id === response.data.property_value.id) {
          return response.data.property_value
        }

        return pv
      })
      updatePropertyValues(postAtProperty.id, updatedPropertyValues)
      const assignees = getAssigneesForUpdate(propertyValue as PropertyValue) || [assignee]

      if (!assignees) return

      const res = await PropertyClient.getBatchAssignedProperties(assignees, [postAtProperty.id])

      if (res && res.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties(res.data.assigned_properties))
      }

      if (isIOSNative) {
        sendMessageToNativeApp('scheduledPostChanged')
      }
    }
  }

  const updateAssignedProperty = async (
    assignedPropertyId: number | undefined,
    property: Property,
    assignedPropertyValues: AssignedPropertyValue[],
  ) => {
    if (!assignedPropertyId) {
      return
    }
    const response = await PropertyClient.updateAssignedProperty<AssignedProperty>(
      assignedPropertyId,
      property,
      assignedPropertyValues,
    )
    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      dispatch(upsertFilesProperties([response.data.assigned_property]))
    }
  }

  const updateStatus = useCallback(
    async (status: PostStatusPropertyValue) => {
      const statusProperty = postStatusProperty
      if (!statusProperty) {
        return
      }

      const propertyValue = statusProperty.property_values.find((value) => value.value === status)

      if (!propertyValue) {
        return
      }

      const assignedPropertyValues = convertPropertyValuesToAssignedPropertyValues(statusProperty, [propertyValue])

      const assignedPostStatusProperty = properties[statusProperty.id]

      // Optimistically set the status locally
      setOptimisticStatus(status)

      try {
        await updateAssignedProperty(
          assignedPostStatusProperty.id,
          statusProperty,
          assignedPropertyValues as AssignedPropertyValue[],
        )

        const promises = targets.map((target) => {
          return updateScheduledPostTarget({
            scheduled_post_id: file?.scheduled_post_id as number,
            target_id: target.id,
            status: STATUS_TO_TARGET_STATUS[status],
          })
        })

        await Promise.all(promises)
        if (isIOSNative) {
          sendMessageToNativeApp('scheduledPostChanged')
        }
      } finally {
        // Unset the optimistic status
        setOptimisticStatus(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postStatusProperty, properties, optimisticStatus],
  )

  const clearPostAt = async () => {
    if (!currentFolderPostAtProperty) {
      return
    }
    const selectedPropertyValues = currentFolderPostAtProperty?.property_values
    if (selectedPropertyValues[0]) {
      await createAndAssignProperty(assignee.id, assignee.type, null)
    }
  }

  const propertyId = currentFolderPostAtProperty?.id || projectPostAtProperty?.id
  const postAt = propertyId ? properties[propertyId]?.values?.[0]?.value : undefined

  const status =
    (optimisticStatus && { id: 1, value: optimisticStatus }) ||
    (postStatusProperty
      ? properties[postStatusProperty.id]?.values?.[0] || { id: 1, value: PostStatusPropertyValue.Draft }
      : {
          id: 1,
          value: PostStatusPropertyValue.Draft,
        })

  return {
    postAt,
    status,
    updatePostAt,
    updateStatus,
    clearPostAt,
  }
}
